import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import privacyPolicyMd from '../../../../docs/policies/privacy-policy.md?raw';
import termsOfUseMd from '../../../../docs/policies/terms-of-use.md?raw';
import listingExtensionPrivacyMd from '../../../../docs/policies/listing-extension-privacy-policy.md?raw';
import { SEO } from '../components/SEO';
import { MarkdownContent } from '../components/marketing/MarkdownContent';

type PolicyType = 'privacy' | 'terms' | 'listing-extension-privacy';

interface PolicyPageProps {
  policy: PolicyType;
}

const POLICY_CONFIG: Record<
  PolicyType,
  { title: string; description: string; path: string; content: string }
> = {
  privacy: {
    title: 'Privacy Policy',
    description:
      'How Resida collects, uses, and protects your personal information when you use our rental management platform.',
    path: '/privacy-policy',
    content: privacyPolicyMd,
  },
  terms: {
    title: 'Terms of Service',
    description:
      'Terms and conditions for using the Resida rental management platform.',
    path: '/terms-of-service',
    content: termsOfUseMd,
  },
  'listing-extension-privacy': {
    title: 'Resida Listing Autofill — Privacy Policy',
    description:
      'What the Resida Listing Autofill Chrome extension accesses, how it uses that information, and what it never does.',
    path: '/privacy-policy/listing-extension',
    content: listingExtensionPrivacyMd,
  },
};

export function PolicyPage({ policy }: PolicyPageProps): JSX.Element {
  const { title, description, path, content } = POLICY_CONFIG[policy];
  const { hash } = useLocation();

  // The markdown renders after the browser's own load-time hash scroll, so jump to the
  // requested section (e.g. #cookies-and-tracking-technologies) once it exists.
  useEffect(() => {
    const id = decodeURIComponent(hash.slice(1));
    if (id) document.getElementById(id)?.scrollIntoView();
  }, [hash, policy]);

  return (
    <div className="min-h-screen bg-[var(--landing-surface)]">
      <SEO title={title} description={description} path={path} />
      <header className="border-b border-[var(--landing-border)] bg-[var(--landing-surface)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <span className="text-xl font-bold text-[var(--landing-accent-text)]">Resida</span>
          <Link
            to="/"
            className="text-sm text-[var(--landing-text-muted)] hover:text-[var(--landing-text-primary)] transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-[var(--landing-text-primary)] mb-8">{title}</h1>
        <MarkdownContent>{content}</MarkdownContent>
      </main>
    </div>
  );
}
